import {Injectable} from '@angular/core';
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {Snapshot} from "../models/snpashot.model";
import {RefundRequestDgSearchResults} from "../models/datagrid-search-results/refund-request.dg-search-results.model";
import {RefundRequest} from "../models/refund-request.model";
import {RefundDto} from "../models/refund-dto.model";
import {RefundValues} from "../models/refund-values.model";

/**
 * Serwis warstwy danych obsługujący operacje finansowe związane ze zwrotami środków.
 * Zarządza procesem tworzenia wniosków o zwrot (Refund Requests), wyliczaniem
 * kwot do zwrotu oraz komunikacją z API płatności w celu zlecenia transferu.
 */
@Injectable({
  providedIn: 'root'
})
export class RefundRequestsService implements DataGridServiceInterface<Observable<RefundRequestDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną listę wniosków o zwrot dla głównego widoku tabelarycznego.
   *
   * @param limit Maksymalna liczba rekordów.
   * @param offset Przesunięcie względem początku.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Kolumna sortująca.
   * @param showDeletedObjects Flaga decydująca o widoczności anulowanych/usuniętych wniosków.
   * @returns Observable emitujący obiekt wyników DataGrid.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<RefundRequestDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/ref-req/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new RefundRequestDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new RefundRequest(), [],
          vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje wnioski o zwrot na podstawie filtrów.
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa zdefiniowanych filtrów.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Kolumna sortująca.
   * @param showDeletedObjects Flaga widoczności anulowanych wniosków.
   * @returns Observable emitujący przefiltrowaną listę dla DataGrid.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>,
                   sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<RefundRequestDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/ref-req/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new RefundRequestDgSearchResults(vetineoApiResponse.response.total,
          this.commonUtils.bestTypedArrayCopyEver(new RefundRequest(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera historię zmian (logi audytowe) dla konkretnego wniosku o zwrot.
   *
   * @param uuid Identyfikator wniosku o zwrot.
   * @returns Observable emitujący tablicę zdarzeń (Snapshot).
   */
  showChanges(uuid: string): Observable<Snapshot[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/ref-req/changes/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Snapshot(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wykonuje fizyczną akcję zwrotu środków do pacjenta.
   *
   * @param dto Obiekt Data Transfer Object zawierający parametry zwrotu.
   * @returns Observable emitujący zaktualizowany obiekt wniosku po udanej autoryzacji zwrotu.
   */
  refund(dto: RefundDto): Observable<RefundRequest> {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/vet-reservations/refund`, dto).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && !vetineoApiResponse.errors) {
        return this.commonUtils.bestTypedCopyEver<RefundRequest>(new RefundRequest(), vetineoApiResponse.response);
      } else {
        throw  new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Anuluje (odrzuca) procesowany wniosek o zwrot środków.
   * Wymaga podania uzasadnienia (powodu anulacji).
   *
   * @param uuid Identyfikator wniosku.
   * @param reason Opis słowny wyjaśniający powód odrzucenia wniosku o zwrot.
   * @returns Observable emitujący zaktualizowany stan wniosku.
   */
  cancel(uuid: any, reason: string) {
    return this.apiService.put<VetineoApiResponse>(`/dashboard/ref-req`, {
      'uuid': uuid,
      'reason': reason
    }).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<RefundRequest>(new RefundRequest(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje wniosek o zwrot powiązany z konkretną, anulowaną rezerwacją wizyty.
   *
   * @param uuid Identyfikator rezerwacji wizyty.
   * @returns Observable emitujący wniosek powiązany z wizytą.
   */
  byVetReservationUUID(uuid: string): Observable<RefundRequest> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/ref-req/by-vr/` + uuid).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<RefundRequest>(new RefundRequest(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wylicza lub pobiera wyliczone procentowe parametry kwotowe dla danego zwrotu.
   *
   * @param uuid Identyfikator wniosku.
   * @returns Observable emitujący wyliczone kwoty (RefundValues).
   */
  getRefundValues(uuid: string) : Observable<RefundValues> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/ref-req/values/` + uuid).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<RefundValues>(new RefundValues(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Inicjuje nowy proces reklamacyjny (tworzy wniosek o zwrot)
   * na podstawie odwołanej rezerwacji.
   *
   * @param uuid Identyfikator rezerwacji.
   * @param reason Powód żądania zwrotu.
   * @returns Observable emitujący utworzony obiekt wniosku.
   */
  createRefundRequestFromReservation(uuid: string, reason: string) : Observable<RefundRequest> {
    return this.apiService.post<VetineoApiResponse>('/dashboard/ref-req-vr',{'uuid': uuid, 'reason': reason}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<RefundRequest>(new RefundRequest(), vetineoApiResponse.response);
      } else {
        throw  new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Wykonuje zwrot środków w kontekście rozliczeń B2B.
   *
   * @param data Obiekt DTO z parametrami zwrotu B2B.
   * @returns Observable potwierdzający wykonanie przelewu zwrotnego.
   */
  refundForClient(data: RefundDto) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/vet-reservations/refund-for-client`, data).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && !vetineoApiResponse.errors) {
        return this.commonUtils.bestTypedCopyEver<RefundRequest>(new RefundRequest(), vetineoApiResponse.response);
      } else {
        throw  new Error(vetineoApiResponse.errors.main);
      }
    }));
  }
}
